export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'off';

export interface LogEntry {
  level: LogLevel;
  message: string;
  context?: string;
  data?: unknown;
  timestamp: Date;
}

export interface LoggerConfig {
  level: LogLevel;
  prefix?: string;
  enableTimestamp?: boolean;
  enableContext?: boolean;
  customHandler?: (entry: LogEntry) => void;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  off: 4
};

const CONSOLE_METHODS: Record<LogLevel, keyof Console | null> = {
  debug: 'debug',
  info: 'info',
  warn: 'warn',
  error: 'error',
  off: null
};

export class Logger {
  private static globalConfig: LoggerConfig = {
    level: 'warn',
    enableTimestamp: true,
    enableContext: true
  };

  private readonly config: LoggerConfig;

  constructor(context?: string, config?: Partial<LoggerConfig>) {
    this.config = {
      ...Logger.globalConfig,
      ...config,
      prefix: context || config?.prefix
    };
  }

  static configure(config: Partial<LoggerConfig>): void {
    Logger.globalConfig = { ...Logger.globalConfig, ...config };
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  private formatMessage(level: LogLevel, message: string): string {
    const parts: string[] = [];

    if (this.config.enableTimestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }

    if (this.config.enableContext && this.config.prefix) {
      parts.push(`[${this.config.prefix}]`);
    }

    parts.push(`${level.toUpperCase()}: ${message}`);

    return parts.join(' ');
  }

  private log(level: LogLevel, message: string, data?: unknown): void {
    if (!this.shouldLog(level)) return;

    const entry: LogEntry = {
      level,
      message,
      context: this.config.prefix,
      data,
      timestamp: new Date()
    };

    if (this.config.customHandler) {
      this.config.customHandler(entry);
      return;
    }

    const formatted = this.formatMessage(level, message);
    const consoleMethod = CONSOLE_METHODS[level];

    if (consoleMethod && console[consoleMethod]) {
      const consoleFn = console[consoleMethod] as (...args: unknown[]) => void;
      if (data !== undefined) {
        consoleFn(formatted, data);
      } else {
        consoleFn(formatted);
      }
    }
  }

  debug(message: string, data?: unknown): void {
    this.log('debug', message, data);
  }

  info(message: string, data?: unknown): void {
    this.log('info', message, data);
  }

  warn(message: string, data?: unknown): void {
    this.log('warn', message, data);
  }

  error(message: string, data?: unknown): void {
    this.log('error', message, data);
  }
}
